import { Flex, Spinner, Text, VStack } from 'native-base';
import React, { ReactNode } from 'react';
import { Platform } from 'react-native';
import WalletManager from 'react-native-wallet-manager';
import { useMutation } from 'react-query';

import AddToAppleWalletButton from 'app/components/AddToAppleWalletButton';
import HTML from 'app/components/HTML';
import useGuest from 'app/hooks/useGuest';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import { getWalletPassUrl, WalletPassURLProps } from 'app/services/GuestCenterService';
import { EntitlementReservation, TicketCategory } from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import { QueryKeys } from 'app/services/QueryClient';
import { tID } from 'app/services/TestHelper';

export type AddReservationToWalletProps = {
  children?: ReactNode;
  reservation: EntitlementReservation;
  passBarcode: string;
  ticketCategory?: TicketCategory;
};

export default function AddReservationToWallet({
  reservation,
  passBarcode: barcode,
  ticketCategory,
}: AddReservationToWalletProps) {
  const { t, locale } = useI18n();
  const { email } = useGuest();
  const { data, mutate, isLoading, isError, isSuccess } = useAddToWalletMutation();

  const addToWallet = () => {
    mutate({
      email,
      walletType: 'apple',
      attractionKey: reservation.attractionKey,
      attractionOrderNumber: reservation.attractionOrderNumber,
      locator: reservation.entitlementKey,
      barcode,
      locale,
      categoryKey: ticketCategory?.categoryKey,
      optionKey: reservation.optionKey,
    });
    Analytics.trackEvent(Events.AttractionAddAppleWallet, {
      productId: reservation.productLineup.contentKey,
      attractionKey: reservation.attractionConfig.contentKey,
    });
  };

  if (!['ios'].includes(Platform.OS)) return null;

  return (
    <VStack alignItems="center" space="2" testID={tID('AddReservationToWallet')}>
      <Flex alignItems="center" h="10" justifyContent="center" mx="auto">
        {isLoading ? <Spinner size="sm" /> : <AddToAppleWalletButton onPress={addToWallet} />}
      </Flex>
      {isSuccess && !!data && <Text fontSize="xs">{t('gen_success')}</Text>}
      {isError && <HTML color="error.500" fontSize="sm" html={`${t('app_error_unknown')}`} />}
    </VStack>
  );
}

function useAddToWalletMutation() {
  return useMutation(QueryKeys.AddToWallet, async (props: WalletPassURLProps) => {
    const url = getWalletPassUrl(props);
    Logger.debug('[AddReservationToWallet] adding pass', { url });
    return WalletManager.addPassFromUrl(url);
  });
}
